import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { CHAINS, getExplorerTxUrl } from '../../services/api';
import { Icons } from './DashboardLayout';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  executed: { label: 'Executed', color: 'text-green-400', bg: 'bg-green-500/10' },
  confirmed: { label: 'Confirmed', color: 'text-green-400', bg: 'bg-green-500/10' },
  failed: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/10' },
  rejected: { label: 'Rejected', color: 'text-red-400', bg: 'bg-red-500/10' },
};

const ACTION_CONFIG = {
  swap: { label: 'Swap', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  transfer: { label: 'Transfer', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  bridge: { label: 'Bridge', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  wrap: { label: 'Wrap', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  unwrap: { label: 'Unwrap', color: 'text-orange-400', bg: 'bg-orange-500/10' },
};

export default function TransactionsPage() {
  const { token, agents } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    loadTransactions();
  }, [token]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const res = await api.getTransactions(token);
      if (res.success) {
        setTransactions(res.data?.transactions || []);
      }
    } catch (err) {
      console.error('Failed to load transactions:', err);
    }
    setLoading(false);
  };

  // Filter transactions
  const filteredTxs = transactions.filter(tx => {
    if (filterAgent && tx.agent_id !== filterAgent) return false;
    if (filterStatus && tx.status !== filterStatus) return false;
    return true;
  });

  if (transactions.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-6">
          <Icons.Transactions size={32} className="text-white/30" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">No Transactions Yet</h3>
        <p className="text-white/50 text-center max-w-md">
          Execute plugin operations to see your transaction history here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={filterAgent}
          onChange={(e) => setFilterAgent(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm outline-none"
        >
          <option value="">All Agents</option>
          {agents.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm outline-none"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="executed">Executed</option>
          <option value="confirmed">Confirmed</option>
          <option value="failed">Failed</option>
        </select>
        <button 
          onClick={loadTransactions}
          className="ml-auto px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white/60 text-sm hover:text-white hover:bg-white/[0.05] transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Transaction List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-[80px] rounded-xl bg-white/[0.02] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTxs.map((tx, index) => (
            <TxRow key={tx.id || index} tx={tx} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}

function TxRow({ tx, index }) {
  const [expanded, setExpanded] = useState(false);
  const action = ACTION_CONFIG[tx.action] || { label: tx.action || 'Transaction', color: 'text-white/60', bg: 'bg-white/5' };
  const status = STATUS_CONFIG[tx.status] || STATUS_CONFIG.pending;
  const chain = CHAINS[tx.chain] || CHAINS.base;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden hover:border-white/[0.10] transition-all"
    >
      {/* Main row */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-9 h-9 rounded-lg bg-black/40 border border-white/[0.08] flex items-center justify-center">
            {chain.logo ? (
              <img src={chain.logo} alt={chain.name} className="w-5 h-5 object-contain" />
            ) : (
              <span className="text-white/50 text-xs">{chain.name?.slice(0, 2)}</span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${action.color} ${action.bg}`}>
                {action.label}
              </span>
              <span className="text-white text-sm">{tx.description || `${action.label} on ${chain.name}`}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-white/40 text-xs">{tx.agent_name || 'Agent'}</span>
              <span className="text-white/20">·</span>
              <span className="text-white/40 text-xs">{formatDate(tx.timestamp || tx.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 rounded-lg text-[10px] font-medium ${status.color} ${status.bg}`}>
            {status.label}
          </span>
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            className="text-white/30"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </motion.div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="px-4 pb-4 pt-2 border-t border-white/[0.06]"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tx.txHash && (
              <div>
                <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Tx Hash</p>
                <a 
                  href={getExplorerTxUrl(tx.chain, tx.txHash)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 text-xs hover:underline font-mono"
                >
                  {tx.txHash.slice(0, 10)}...{tx.txHash.slice(-8)}
                </a>
              </div>
            )}
            {tx.gasUsed && (
              <div>
                <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Gas Used</p>
                <p className="text-white text-xs">{tx.gasUsed}</p>
              </div>
            )}
            {tx.value && (
              <div>
                <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Value</p>
                <p className="text-white text-xs">{tx.value}</p>
              </div>
            )}
            <div>
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Chain</p>
              <div className="flex items-center gap-1.5">
                {chain.logo && <img src={chain.logo} alt="" className="w-3.5 h-3.5" />}
                <span className="text-white text-xs">{chain.name}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
